import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, FileText, Calendar, AlertCircle, TrendingUp, DollarSign, ChevronRight, Sparkles, ClipboardList, Shield } from "lucide-react";
import { Page, StatCard, Card, Pill } from "../components/UI";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/clients").then((r) => setClients(r.data.slice(0, 5))).catch(() => {}),
      api.get("/appointments").then((r) => setAppts(r.data.filter(a => a.status === "scheduled").slice(0, 4))).catch(() => {}),
    ]);
  }, []);

  const quickActions = [
    { to: "/notes", label: "Write Note", icon: FileText, color: "var(--mf-primary)" },
    { to: "/treatment-plans", label: "Treatment Plan", icon: ClipboardList, color: "var(--mf-secondary)" },
    { to: "/scheduling", label: "Schedule", icon: Calendar, color: "var(--mf-accent)" },
    { to: "/safety-plans", label: "Safety Plan", icon: Shield, color: "#B45253" },
  ];

  return (
    <Page
      title={`Good ${new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, ${user?.full_name?.split(" ")[0] || "Clinician"}`}
      subtitle="Here's your calm, clear view of the day."
    >
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Active Clients" value={stats?.total_clients ?? "—"} icon={Users} accent="var(--mf-primary)" delay={0.05} testid="stat-clients" />
        <StatCard label="Notes this Month" value={stats?.total_notes ?? "—"} icon={FileText} accent="var(--mf-secondary)" delay={0.1} testid="stat-notes" />
        <StatCard label="Appointments" value={stats?.total_appointments ?? "—"} icon={Calendar} accent="var(--mf-accent)" delay={0.15} testid="stat-appointments" />
        <StatCard label="High-Risk Clients" value={stats?.high_risk_clients ?? "—"} icon={AlertCircle} accent="#B45253" delay={0.2} testid="stat-risk" />
      </div>

      {/* Revenue strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="MRR" value={`$${stats?.mrr ?? 0}`} icon={DollarSign} accent="var(--mf-primary)" delay={0.25} testid="stat-mrr" />
        <StatCard label="Collections Rate" value={`${Math.round((stats?.collections_rate ?? 0) * 100)}%`} icon={TrendingUp} accent="var(--mf-secondary)" delay={0.3} testid="stat-collections" />
        <StatCard label="Pending Prior Auth" value={stats?.pending_auth ?? 0} icon={ClipboardList} accent="var(--mf-accent)" delay={0.35} testid="stat-pending-auth" />
      </div>

      {/* Quick Actions */}
      <Card delay={0.4} testid="quick-actions-card" className="mb-8">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-5 h-5" style={{ color: "var(--mf-accent)" }} strokeWidth={1.5} />
          <h2 className="font-serif text-2xl">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.map((a) => (
            <Link key={a.to} to={a.to} className="group p-5 rounded-2xl transition-all hover:-translate-y-1" style={{ background: "var(--mf-muted)" }} data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" style={{ background: a.color }}>
                <a.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div className="font-medium text-sm" style={{ color: "var(--mf-text)" }}>{a.label}</div>
            </Link>
          ))}
        </div>
      </Card>

      {/* Recent clients + upcoming */}
      <div className="grid md:grid-cols-2 gap-5">
        <Card delay={0.45} testid="recent-clients-card">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl">Recent Clients</h2>
            <Link to="/clients" className="text-sm flex items-center gap-1" style={{ color: "var(--mf-primary)" }} data-testid="view-all-clients">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {clients.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: "var(--mf-text-muted)" }}>No clients yet. <Link to="/clients" className="underline" style={{ color: "var(--mf-primary)" }}>Add your first client</Link>.</div>
          ) : (
            <div className="space-y-2">
              {clients.map((c) => (
                <Link key={c.id} to={`/clients/${c.id}`} className="flex items-center justify-between p-3 rounded-xl transition-all hover:bg-black/[0.03]" data-testid={`recent-client-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium text-white" style={{ background: "var(--mf-primary)" }}>
                      {c.first_name?.[0]}{c.last_name?.[0]}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{c.first_name} {c.last_name}</div>
                      <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{c.diagnosis || "No diagnosis"}</div>
                    </div>
                  </div>
                  <Pill variant={c.risk_level === "high" ? "danger" : c.risk_level === "moderate" ? "warn" : "success"}>{c.risk_level}</Pill>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card delay={0.5} testid="upcoming-appts-card">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl">Upcoming Sessions</h2>
            <Link to="/scheduling" className="text-sm flex items-center gap-1" style={{ color: "var(--mf-primary)" }} data-testid="view-schedule">
              Schedule <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {appts.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: "var(--mf-text-muted)" }}>No upcoming sessions. <Link to="/scheduling" className="underline" style={{ color: "var(--mf-primary)" }}>Create one</Link>.</div>
          ) : (
            <div className="space-y-2">
              {appts.map((a) => (
                <div key={a.id} className="p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-sm">{new Date(a.start_time).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                    <Pill variant="primary">{a.appointment_type}</Pill>
                  </div>
                  {a.notes && <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{a.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}
